// What a Security Plus gate hands to the code it protects, and the helpers
// that log a write under it. Free of React contexts, so write paths (and their
// tests) can use it without pulling in the PIN hook (CustomHooks/usePinAuth).
import type { SecurityPlusKeys } from 'CustomHooks/usePinAuth';
import {
  ActivityCategory,
  ActivityChange,
  ActivityDetails,
  ActivityOutcome,
  ActivityTarget,
  AuthorizationMethod,
} from './events';

/**
 * What one Security Plus gate let through, and how. Passed to the protected
 * callback so the business write it starts can be logged when it completes.
 */
export type SecurityGrant = {
  permission: SecurityPlusKeys;
  method: AuthorizationMethod;
  pinProfile: string | null;
  authorizationId: string;
  /** Logs an activity done with this grant (e.g. a print started later). */
  record: (
    category: ActivityCategory,
    action: string,
    outcome: ActivityOutcome,
    details?: ActivityDetails
  ) => void;
  /** Logs the outcome of a write once it settled: succeeded or failed, never early. */
  track: <T>(action: string, write: Promise<T>, details?: TrackDetails<T>) => Promise<T>;
};

/** Details of a tracked write; the result can add to them once it arrived. */
export type TrackDetails<T> = ActivityDetails & {
  fromResult?: (result: T) => ActivityDetails & { outcome?: ActivityOutcome };
};

/**
 * What a gate does. view/print/override/form are logged as such when the gate
 * opens; a change gate is logged as an authorization, its write separately.
 */
export type GateActivity = {
  action?: string;
  category?: Exclude<ActivityCategory, 'authorization'>;
  targets?: ActivityTarget[];
  changes?: ActivityChange[];
};

/** For writes that resolve `false` when the server did not apply them. */
export const unlessRefused = (details: ActivityDetails = {}): TrackDetails<unknown> => ({
  ...details,
  fromResult: (applied) =>
    applied === false ? { outcome: 'failed', reason: 'not_applied' } : {},
});

/** Without a grant (Security Plus did not gate the action) nothing is logged. */
export const trackChange = <T>(
  grant: SecurityGrant | undefined,
  action: string,
  write: Promise<T>,
  details?: TrackDetails<T>
) => (grant ? grant.track(action, write, details) : write);
